import { useEffect } from 'react';
import { useMatch, type RouteObject } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentProfile } from '@/hooks/useCurrentProfile';
import { useCurrentUserOrgRole } from '@/hooks/useCurrentUserOrgRole';
import { useTranslation } from '@/hooks/useTranslation';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAppMode } from '@/contexts/AppModeContext';
import { useIsSuperAdmin } from '@/lib/permissions';
import {
  identifyPostHogUser,
  initPostHog,
  optOutOfPostHog,
  setPostHogContext,
  setPostHogOrganization,
} from '@/lib/posthog';

interface PostHogIdentityProps {
  /** The app's route tree, for URL → route-pattern redaction. */
  routes: RouteObject[];
}

/**
 * The single place PostHog learns who is using the app (docs/OBSERVABILITY.md
 * "Product Analytics (PostHog)"). Renders nothing. Mounted inside
 * AppModeProvider (it reads the app mode).
 *
 * PostHog starts only once a signed-in user's profile has loaded and they
 * have not objected (`profiles.analytics_opt_out`): pre-login pages are never
 * captured, and an opted-out user never loads it. A profile that fails to
 * load leaves it off.
 *
 * Never on a public token page (a survey's `/s/:token`, a volunteer signup
 * page's `/v/:token`): staff are not counted as respondents, and an
 * identified init there would carry the raw token URL in the SDK's `/flags`
 * request (outside before_send). The init waits until the user navigates
 * into the app; signed-out respondents get PublicSurvey's anonymous instance
 * instead.
 */
export function PostHogIdentity({ routes }: PostHogIdentityProps) {
  const { user } = useAuth();
  const { data: profile } = useCurrentProfile();
  const { selectedOrganization } = useOrganization();
  const { role: orgRole } = useCurrentUserOrgRole();
  const { mode: appMode } = useAppMode();
  const { data: isSuperAdmin } = useIsSuperAdmin();
  const { i18n } = useTranslation();

  const userId = user?.id ?? null;
  const profileLoaded = !!userId && !!profile;
  const optedOut = profile?.analytics_opt_out ?? false;
  const email = profile?.email ?? user?.email ?? null;
  const name = profile?.display_name ?? null;
  const isInternal = isSuperAdmin === true;
  // Wait for the super-admin answer (cached from the header) so staff are
  // tagged is_internal from their very first event, never counted as customers.
  const internalKnown = isSuperAdmin !== undefined;
  // Public token pages (a survey's `/s/:token`, a volunteer signup page's `/v/:token`) are never identified.
  const onSurveyPage = useMatch('/s/:token') !== null;
  const onVolunteerPage = useMatch('/v/:token') !== null;
  const onPublicPage = onSurveyPage || onVolunteerPage;
  const active = profileLoaded && internalKnown && !optedOut && !onPublicPage;

  const organizationId = selectedOrganization?.id ?? null;
  const organizationName = selectedOrganization?.name ?? null;
  const locale = i18n.language ? i18n.language.split('-')[0] : null;

  // Declared first: the per-event context is in place before PostHog starts,
  // so even the first $identify / $pageview carry the org and role.
  useEffect(() => {
    setPostHogContext(
      userId
        ? {
            organization_id: organizationId,
            organization_name: organizationName,
            org_role: orgRole,
            app_mode: appMode,
            locale,
            is_internal: isInternal,
          }
        : null,
    );
  }, [userId, organizationId, organizationName, orgRole, appMode, locale, isInternal]);

  useEffect(() => {
    if (!userId) {
      identifyPostHogUser(null); // sign-out: reset (no-op if never started)
      return;
    }
    if (!profileLoaded) return;
    if (optedOut) {
      optOutOfPostHog();
      return;
    }
    if (!internalKnown || onPublicPage) return;
    initPostHog(routes);
    identifyPostHogUser(userId, { email, name, isInternal });
  }, [routes, userId, profileLoaded, optedOut, internalKnown, onPublicPage, email, name, isInternal]);

  // After the init effect: on the commit that starts PostHog, init runs
  // first, then the group.
  useEffect(() => {
    if (!active) return;
    setPostHogOrganization(organizationId, organizationName);
  }, [active, organizationId, organizationName]);

  return null;
}
